import React from "react";
import type { Dispatch } from "redux";
import { connect, ReactReduxContext } from "react-redux";

import GanttChart from "../ganttChart/GanttChart";
import Controls from "../interaction/Controls";
import ShowGanttOrderDataOnClick from "../modals/ShowGanttOrderDataOnClick";

import * as t from "@/lib/backend/manufacturing2.generated.types";
import { type IReportModelEx } from "@/lib/simulationReportModels";

import { UPDATE_PROPERTIES_ACTION_NAME, GanttChartSource } from "../reducers/gantt/properties";
import { UPDATE_INSIGHTS_ACTION_NAME } from "../reducers/planningTable/insights";
import { PropertiesLogic } from "../reducers/logic/businessLogic";
import { REPORT_LOADING_ACTION_NAME } from "../reducers/report";
import { UPDATE_REPORT_ACTION_NAME } from "../reducers/common";
import { UPDATE_ORDERS } from "../reducers/gantt/ganttChart";
import { onSaveComment } from "../modals/PlanningTableEditModal";
import { getBackend } from "@/lib/backend/backend2";
import { UPDATE_MATERIALS } from "../reducers/gantt/ganttChart";

import type { ActionTypes, ReduxState } from "../reducers/reducers";
import type { PropertiesState, GanttChartSourceTypes } from "../reducers/gantt/properties";
import type { IOrderModelBase } from "@/lib/backend/manufacturing2.generated.types";

export type GanttChartContainerProps = {
    handleClick: (line_uuid: string) => void;
    insights: t.IEventDataEx[];
    insight_types: string[];
    report: IReportModelEx;
    show_past_productions: boolean;
    current_shift?: number;
    past_weeks: number;
    days: number;
    week?: number;
    year: number;
    simulation_uuid: string;
    space_for_label?: number;
    show_labels?: boolean;
    top_padding?: number;
    bar_height?: number;
    space_between_bars?: number;
    no_overflow?: boolean;
    show_only_tool_changes: boolean;
    line_group_uuid: string;
    source_type: GanttChartSourceTypes;
    show_only_unscheduled?: boolean;
    disable_zoom?: boolean;
    onOrderChange?: (order: t.IOrderModelBase) => void;
};

type OwnProps = GanttChartContainerProps;
type Props = OwnProps & ReduxProps;

class GanttChartContainer extends React.Component<Props> {
    static contextType = ReactReduxContext;
    context!: React.ContextType<typeof ReactReduxContext>;

    async componentDidMount() {
        this.updateReport();
        this.updateProperties();
        this.updateInsights();
        this.loadMaterials();
        this.loadOrders();
    }

    async componentDidUpdate(prev_props: Props) {
        if (
            prev_props.current_shift !== this.props.current_shift ||
            prev_props.past_weeks !== this.props.past_weeks ||
            prev_props.week !== this.props.week ||
            prev_props.year !== this.props.year ||
            prev_props.space_for_label !== this.props.space_for_label ||
            prev_props.top_padding !== this.props.top_padding ||
            prev_props.bar_height !== this.props.bar_height ||
            prev_props.space_between_bars !== this.props.space_between_bars ||
            prev_props.no_overflow !== this.props.no_overflow ||
            prev_props.show_only_tool_changes !== this.props.show_only_tool_changes ||
            prev_props.source_type !== this.props.source_type
        ) {
            this.updateProperties();
        }

        if (prev_props.report !== this.props.report) {
            this.updateReport();
            this.loadMaterials();
            this.loadOrders();
        }

        if (prev_props.insights !== this.props.insights) {
            this.updateInsights();
        }
    }

    updateReport = () => {
        this.props.reduxDispatch({ type: UPDATE_REPORT_ACTION_NAME, data: this.props.report });
        this.props.reduxDispatch({ type: REPORT_LOADING_ACTION_NAME, data: false });
    };

    updateProperties = () => {
        const properties: Partial<PropertiesState> = {
            current_shift: this.props.current_shift,
            gantt_past_weeks: this.props.past_weeks,
            week: this.props.week,
            year: this.props.year,
            space_for_label: this.props.space_for_label,
            show_labels: this.props.show_labels,
            top_padding: this.props.top_padding,
            bar_height: this.props.bar_height,
            space_between_bars: this.props.space_between_bars,
            no_overflow: this.props.no_overflow,
            past_weeks: this.props.past_weeks,
            show_only_tool_changes: this.props.show_only_tool_changes,
            count_non_empty_lines: this.props.report.result.orders.length,
            source_type: this.props.source_type ? this.props.source_type : GanttChartSource.gantt,
            line_group_uuid: this.props.line_group_uuid,
            show_past_productions: this.props.past_weeks > 0 ? this.props.show_past_productions : false,
            show_only_unscheduled: this.props.show_only_unscheduled,
            num_shifts: this.props.report.result.line_shift_features[0].shifts.length,
            disable_zoom: this.props.disable_zoom,
        };

        properties.start_day = PropertiesLogic.getStartDay(
            properties as PropertiesState,
            Math.floor(this.props.report.result.next_shift_time.shift_number / 3)
        );

        this.props.reduxDispatch({ type: UPDATE_PROPERTIES_ACTION_NAME, data: properties });
    };

    updateInsights = () => {
        this.props.reduxDispatch({
            type: UPDATE_INSIGHTS_ACTION_NAME,
            data: {
                insights: this.props.insights,
                insight_types: this.props.insight_types,
            },
        });
    };

    loadMaterials = async () => {
        const lines_orders = this.props.report.result.orders;
        const material_external_ids: Set<string> = new Set();
        for (const line_orders of lines_orders) {
            for (const order of line_orders.production) {
                material_external_ids.add(order.material);
            }
        }

        try {
            const res_materials = await getBackend().manufacturing.searchMaterialsSimple({
                external_ids: [...material_external_ids],
            });
            this.props.reduxDispatch({ type: UPDATE_MATERIALS, data: res_materials.data });
        } catch (error) {
            console.error(error);
        }
    };

    loadOrders = async () => {
        const lines_orders = this.props.report.result.orders;
        const uuids: string[] = [];
        for (const line_orders of lines_orders) {
            for (const order of line_orders.production) {
                uuids.push(order.order_id);
            }
        }

        try {
            const { operations } = await getBackend().manufacturing.getOrdersSimple({ uuids });
            this.props.reduxDispatch({ type: UPDATE_ORDERS, data: operations });
        } catch (error) {
            console.error(error);
        }
    };

    onOrderChange = (order?: IOrderModelBase) => {
        if (!order) {
            return;
        }
        const comment = order.order_comment || "";
        onSaveComment(order.uuid, comment, this.context.store);
        if (this.props.onOrderChange) {
            this.props.onOrderChange(order);
        }
    };

    render() {
        return (
            <Controls>
                <ShowGanttOrderDataOnClick onOrderChange={this.onOrderChange} report={this.props.report.result} />
                <GanttChart
                    key={"gantt-chart-container"}
                    line_group_uuid={this.props.line_group_uuid}
                    source_type={this.props.source_type}
                />
            </Controls>
        );
    }
}

const mapState = (state: ReduxState) => ({
    insight_types: state.standalone_gantt.insight_types,
});

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({ reduxDispatch: dispatch });

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(GanttChartContainer);
